import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';
import { EnumGenericRoleActionAlerts } from '@pages/BasePage';
import { EnumActionsPermissions } from '@models/EnumPermissions';
import { EnumHouseholdAssociationStatus, IConsumerCardSearchFilterModel, IConsumerCardSearchModel } from '@models/Consumers';
import { FormValidators } from '@validators/form.validators';
import { StringUtils } from '@utils/StringUtils';

/**
 * Um agregado da lista, com os respectivos membros já ordenados.
 * Espelha a interface homónima da CrudPage — é a página que agrupa e ordena,
 * a modal só apresenta e devolve o que recebeu.
 */
export interface IHouseHoldGroup {
    IdHouseHold: string;
    Label: string;
    IsProfileMain: boolean;
    Members: any[];
}

/**
 * Campo pelo qual se pesquisa o cartão a associar. Só um está activo de cada vez.
 */
export enum EnumConsumerCardSearchField {
    VatNumber = 1,
    CardNumber = 2
}

/** separador visível da modal — mesmo padrão do select-manager-profile */
export enum EnumHouseholdModalMode {
    Members = 'members',
    Associate = 'associate'
}

/** estados ativos: são os únicos que podem ser cancelados */
const HOUSEHOLD_ACTIVE_STATUS: number[] =
    [
        EnumHouseholdAssociationStatus.Accepted,
        EnumHouseholdAssociationStatus.Pending
    ];

/**
 * Gestão completa do agregado familiar de um consumidor: consulta e gere os membros
 * atuais (responsável, cancelar associação) e associa um novo cartão a um agregado
 * existente ou a um agregado novo — tudo numa única modal, sem popovers/alertas em
 * cadeia. As acções que mexem em dados ficam a cargo da página, por callback: a
 * modal só apresenta o resultado que cada callback devolve.
 */
@Component({
    selector: 'app-associate-household',
    templateUrl: './associate-household.component.html',
    styleUrls: ['./associate-household.component.scss'],
})
export class AssociateHouseHoldModalComponent {

    public Title: string = 'Gestão do agregado';

    public Groups: IHouseHoldGroup[] = [];

    public EnumActionsPermissions = EnumActionsPermissions;

    public EnumConsumerCardSearchField = EnumConsumerCardSearchField;

    public EnumHouseholdModalMode = EnumHouseholdModalMode;

    /** separador ativo — membros atuais ou associar novo cartão */
    public Mode: EnumHouseholdModalMode = EnumHouseholdModalMode.Members;

    /**
     * Destino do cartão: um agregado existente XOR um agregado novo — nunca os dois
     * na mesma escolha, para não misturar "selecionar" com "criar" numa só lista.
     */
    public IsNewHouseHold: boolean = false;

    /** agregado existente escolhido como destino, quando !IsNewHouseHold */
    public SelectedHouseHoldId: string = null;

    public NewHouseHoldName: string = '';

    public SearchForm: FormGroup;

    public SearchData: IConsumerCardSearchModel[] = [];

    public SelectedCardId: string = null;

    public HasSearched: boolean = false;

    private onToggleMain: (item: any) => Promise<IHouseHoldGroup[]>;
    private onRemoveMember: (item: any) => Promise<IHouseHoldGroup[]>;
    private onViewMember: (item: any) => void;
    private onSearchCard: (filters: IConsumerCardSearchFilterModel) => Promise<IConsumerCardSearchModel[]>;
    private onAssociate: (idHouseHold: string, houseHoldName: string, cardNumber: string) => Promise<IHouseHoldGroup[]>;
    private showToast: (text: string) => void;

    /** consumidor cujo perfil está aberto; não faz sentido associá-lo a si próprio */
    private currentConsumer: IConsumerCardSearchFilterModel = {};

    /** id do consumidor do perfil — identifica a sua própria linha nas tabelas de membros */
    private loadedIdUser: string = null;

    constructor(
        private NavParams: NavParams,
        private ModalController: ModalController) {

        this.Groups = this.NavParams.get('groups') || [];
        this.currentConsumer = this.NavParams.get('currentConsumer') || {};
        this.loadedIdUser = this.NavParams.get('loadedIdUser') || null;

        this.onToggleMain = this.NavParams.get('onToggleMain') || null;
        this.onRemoveMember = this.NavParams.get('onRemoveMember') || null;
        this.onViewMember = this.NavParams.get('onViewMember') || null;
        this.onSearchCard = this.NavParams.get('onSearchCard') || null;
        this.onAssociate = this.NavParams.get('onAssociate') || null;
        this.showToast = this.NavParams.get('showToast') || null;

        // sem agregados de que o consumidor seja responsável, o único destino possível
        // é um agregado novo — nem faz sentido mostrar a escolha
        this.IsNewHouseHold = this.TargetGroups.length == 0;
        this.SelectedHouseHoldId = this.TargetGroups.length ? this.TargetGroups[0].IdHouseHold : null;

        this.SearchForm = new FormGroup({
            SearchType: new FormControl(EnumConsumerCardSearchField.VatNumber),
            VatNumber: new FormControl(null, [FormValidators.NIFValidator]),
            CardNumber: new FormControl(null, [FormValidators.NumberValidator])
        });
    }

    /** agregados de que o consumidor do perfil é responsável — só esses podem receber um novo cartão */
    public get TargetGroups(): IHouseHoldGroup[] {

        return (this.Groups || []).filter(it => it?.IsProfileMain);
    }

    public OnSelectTarget(id: string) {

        this.SelectedHouseHoldId = id;
    }

    /**
     * Alterna entre "agregado existente" e "agregado novo" — mutuamente exclusivos.
     * @param isNew
     */
    public OnSelectHouseHoldMode(isNew: boolean) {

        this.IsNewHouseHold = isNew;

        // ao voltar para "existente" sem nada escolhido, cai no primeiro por omissão
        if (isNew || this.SelectedHouseHoldId) return;

        this.SelectedHouseHoldId = this.TargetGroups[0]?.IdHouseHold || null;
    }

    /**
     * Troca de separador (ionChange do ion-segment).
     * @param ev
     */
    public OnChangeMode(ev: any) {

        this.Mode = ev?.detail?.value;
    }

    /**
     * Estrela: definir/retirar responsável. O alerta de confirmação e o toast de
     * resultado já são tratados no callback, do lado da página — aqui só se refresca
     * a lista com o que ele devolver.
     * @param ev
     * @param item
     */
    public async OnToggleMain(ev: any, item: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        if (!this.onToggleMain) return;

        const groups = await this.onToggleMain(item);

        if (groups) this.Groups = groups;
    }

    /**
     * Cruz: cancelar associação ao agregado.
     * @param ev
     * @param item
     */
    public async OnRemoveMember(ev: any, item: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        if (!this.onRemoveMember) return;

        const groups = await this.onRemoveMember(item);

        if (groups) this.Groups = groups;
    }

    public OnViewMember(ev: any, item: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        if (this.onViewMember) this.onViewMember(item);
    }

    public CardNumber(number: string): string {

        return number ? StringUtils.FormatToCreditCardNumber(number) : '--';
    }

    /**
     * A linha é a do próprio consumidor do perfil? Mesma verificação da página —
     * esconde o botão de ver perfil nessa linha (não faz sentido ver o próprio).
     * @param item
     */
    public IsProfileMember(item: any): boolean {

        return !!item?.IdUser && item?.IdUser?.toLowerCase() == this.loadedIdUser?.toLowerCase();
    }

    /**
     * Uma associação ativa (aceite ou pendente) é a única que pode ser cancelada.
     * @param item
     */
    public IsHouseHoldMemberActive(item: any): boolean {

        return HOUSEHOLD_ACTIVE_STATUS.some(it => it == Number(item?.Status?.Id));
    }

    /**
     * Só um membro que já aceitou a associação pode ser responsável do agregado —
     * um pendente ainda não confirmou que faz parte dele.
     * @param item
     */
    public IsHouseHoldMemberAccepted(item: any): boolean {

        return Number(item?.Status?.Id) == EnumHouseholdAssociationStatus.Accepted;
    }

    /**
     * Nome do controlo do campo activo.
     */
    private get activeControlName(): 'VatNumber' | 'CardNumber' {

        return this.SearchForm.get('SearchType')?.value == EnumConsumerCardSearchField.CardNumber ? 'CardNumber' : 'VatNumber';
    }

    /**
     * Texto do campo activo, para as mensagens.
     */
    private get activeFieldName(): string {

        return this.activeControlName == 'CardNumber' ? 'o nº do cartão' : 'o NIF';
    }

    /** descrição do estado vazio, antes da primeira pesquisa */
    public get EmptyDescription(): string {

        return `Indique ${this.activeFieldName}`;
    }

    public IsSearchType(type: EnumConsumerCardSearchField): boolean {

        return this.SearchForm.get('SearchType')?.value == type;
    }

    /**
     * Troca o campo de pesquisa. O valor escrito no anterior não se aproveita —
     * um NIF não é um nº de cartão — por isso limpa-se tudo, tal como os resultados.
     * @param ev
     * @param type
     */
    public OnChangeSearchType(ev: any, type: EnumConsumerCardSearchField) {

        ev?.preventDefault();
        ev?.stopPropagation();

        if (this.IsSearchType(type)) return;

        this.SearchForm.reset({ SearchType: type });

        this.OnClearResults();
    }

    /**
     * Só o campo activo conta.
     */
    public get HasFilter(): boolean {

        return !!this.filterValue(this.activeControlName);
    }

    public async OnSearch(ev?: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        this.SearchForm.markAllAsTouched();

        const controlName = this.activeControlName;

        if (!this.HasFilter) {

            if (this.showToast)
                this.showToast(`Indique ${this.activeFieldName}`);

            return;
        }

        if (this.SearchForm.get(controlName).invalid) {

            if (this.showToast)
                this.showToast(controlName == 'CardNumber'
                    ? 'O nº de cartão introduzido não está válido'
                    : 'O NIF introduzido não está válido');

            return;
        }

        // só vai o campo escolhido; o outro nem sequer está preenchido
        const filters: IConsumerCardSearchFilterModel =
        {
            VatNumber: controlName == 'VatNumber' ? this.filterValue('VatNumber') : null,
            CardNumber: controlName == 'CardNumber' ? this.filterValue('CardNumber') : null
        };

        if (this.filterValue(controlName) == this.currentConsumerValue(controlName)) {

            if (this.showToast)
                this.showToast(controlName == 'CardNumber'
                    ? 'O nº de cartão indicado é o do próprio consumidor'
                    : 'O NIF indicado é o do próprio consumidor');

            return;
        }

        if (!this.onSearchCard) return;

        this.SearchData = await this.onSearchCard(filters) || [];

        this.SelectedCardId = null;

        this.HasSearched = true;

        // aviso imediato — não basta a linha aparecer esbatida, o utilizador tem de
        // perceber já, sem precisar de clicar, que aquele resultado não pode ser associado
        if (this.showToast && this.SearchData.some(it => this.IsAlreadyMember(it)))
            this.showToast('Este membro já pertence ao agregado');
    }

    /**
     * Limpa os campos, os resultados e a selecção.
     * @param ev
     */
    public OnClear(ev?: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        // limpar não muda o campo escolhido
        this.SearchForm.reset({ SearchType: this.SearchForm.get('SearchType')?.value });

        this.OnClearResults();
    }

    /**
     * Limpa apenas os resultados. O botão de limpar da searchbar já limpa o
     * respectivo campo, e limpar um campo não deve apagar o outro.
     */
    public OnClearResults() {

        this.SearchData = [];
        this.SelectedCardId = null;
        this.HasSearched = false;
    }

    /**
     * O nº de cartão pode vir formatado com espaços (leitura, colar do cartão físico) —
     * removem-se em tempo real, tal como em /merchants/movements, para não falhar a
     * validação nem a comparação com o valor pesquisado.
     * @param ev
     */
    public OnCleanCardNumberInput(ev: any) {

        const value = ev?.detail?.value || '';

        this.SearchForm.get('CardNumber').patchValue(value.replace(/\s+/g, ''));
    }

    /**
     * Selecção única, com desselecção ao reclicar na mesma linha. Quem já pertence
     * ao agregado não pode ser reassociado — o único caminho para o retirar é a
     * cruz da secção "Membros atuais".
     * @param item
     */
    public OnSelectCard(item: IConsumerCardSearchModel) {

        if (this.IsAlreadyMember(item)) {

            if (this.showToast) this.showToast('Este membro já pertence ao agregado');

            return;
        }

        this.SelectedCardId = this.SelectedCardId == item?.Id ? null : item?.Id;
    }

    public IsSelectedCard(item: IConsumerCardSearchModel): boolean {

        return !!item?.Id && this.SelectedCardId == item?.Id;
    }

    /**
     * O consumidor do resultado já é membro de algum dos agregados listados —
     * associá-lo outra vez duplicaria a linha em "Membros atuais".
     * Compara por IdUser e por nº de cartão: o serviço de pesquisa (WebService.SearchConsumer)
     * devolve `any`, sem garantia de que o IdUser vem sempre preenchido — o nº de cartão é o
     * único dado que sabemos existir sempre dos dois lados (é o que já se mostra na tabela).
     * @param item
     */
    public IsAlreadyMember(item: IConsumerCardSearchModel): boolean {

        const idUser = item?.IdUser?.toLowerCase() || null;
        const cardNumber = StringUtils.RemoveWhiteSpace(item?.CardNumber || '') || null;

        if (!idUser && !cardNumber) return false;

        return (this.Groups || []).some(group => (group?.Members || []).some(member => {

            if (idUser && member?.IdUser?.toLowerCase() == idUser) return true;

            const memberCardNumber = StringUtils.RemoveWhiteSpace(member?.WalletCard?.Number || '') || null;

            return !!cardNumber && !!memberCardNumber && memberCardNumber == cardNumber;
        }));
    }

    /**
     * Submete a associação do cartão escolhido ao agregado escolhido (ou a um novo).
     * Sucesso refresca a lista de membros e limpa a secção de pesquisa, mas a modal
     * fica aberta — é a superfície de gestão do agregado, não um passo único.
     * @param ev
     */
    public async OnConfirmAssociate(ev?: any) {

        ev?.preventDefault();
        ev?.stopPropagation();

        const selected = this.SearchData.find(it => it?.Id == this.SelectedCardId) || null;

        if (!selected) {

            if (this.showToast) this.showToast('Selecione um cartão');

            return;
        }

        if (this.IsAlreadyMember(selected)) {

            if (this.showToast) this.showToast('Este membro já pertence ao agregado');

            return;
        }

        if (!this.onAssociate) return;

        const idHouseHold = this.IsNewHouseHold ? null : this.SelectedHouseHoldId;
        const houseHoldName = this.IsNewHouseHold ? (this.NewHouseHoldName || '').trim() : null;

        const groups = await this.onAssociate(idHouseHold, houseHoldName, selected.CardNumber);

        if (!groups) return;

        this.Groups = groups;

        this.SelectedCardId = null;
        this.SearchData = [];
        this.HasSearched = false;
        this.NewHouseHoldName = '';
    }

    public Close() {

        this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }

    private filterValue(controlName: string): string {

        return StringUtils.RemoveWhiteSpace(this.SearchForm.get(controlName)?.value || '') || null;
    }

    /**
     * O nº de cartão do consumidor vem formatado com espaços, por isso compara-se sempre
     * sem espaços, à semelhança do que se faz aos valores introduzidos.
     * @param field
     * @returns
     */
    private currentConsumerValue(field: 'VatNumber' | 'CardNumber'): string {

        return StringUtils.RemoveWhiteSpace(this.currentConsumer?.[field] || '') || null;
    }
}
